/**
 * The page a password-reset link lands on.
 *
 * In plain words: this is where somebody ends up after clicking the link in the reset email.
 * It asks for a new password twice and will not let go until one is set or the person
 * cancels. It is also the page that has to say "that link is dead" when it is, rather than
 * quietly offering a form that can only do the wrong thing.
 *
 * THREE GATES, in this order, before anything else can render:
 *
 *  1. Did Supabase put an error in the URL fragment? Then the link is expired, already used
 *     or malformed. Say so in this page's own words, never in the wording the fragment
 *     carries, which anyone can write (D-044), and offer the request form again.
 *     The session is deliberately NOT consulted here. If it were, somebody already signed in
 *     who clicks a dead link would be shown a working password form, and would change the
 *     password of the account signed in on that browser while believing they were recovering
 *     another one (FR-009, SC-006).
 *  2. Is this browser actually part-way through a reset, for this user? If not, the visitor
 *     goes to the dashboard at /app. They are not offered the form: changing a password while
 *     already signed in is a non-goal of this feature, and an ungated page would ship it
 *     through the back door at a guessable URL on a shared machine (FR-004, SC-007). The
 *     signed-in user's id is passed in, which is what stops one person's unfinished reset
 *     following the next person who signs in on the same browser (FR-013).
 *  3. In recovery, but there is no session? Then the link's token exchange failed, and the
 *     form could only ever answer "Auth session missing!". Show the dead-link state and the
 *     way back instead of a form that cannot work (FR-013).
 *
 * Then, and only then, the form.
 *
 * Requirements: FR-004, FR-006, FR-007, FR-008, FR-009, FR-010.
 */

import { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { authClient } from "@/services/auth";
import type { AuthError } from "@/services/auth/types";
import {
  APP_HOME,
  BOOT_HASH,
  detectRecoveryErrorFromUrl,
  endRecovery,
  isInRecovery,
} from "@/services/auth/recovery";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

/** Where cancel and the dead-link state send people: the login card. */
const LOGIN_PATH = "/auth";

/**
 * Stricter than signup, which enforces no length at all. Accepted as a known gap in the
 * spec: the user is choosing a *new* password here, so nothing is lost.
 */
const MIN_PASSWORD_LENGTH = 8;

/**
 * Read the link's verdict once, from the fragment as it looked when the app started.
 * `BOOT_HASH` exists because supabase-js clears `window.location.hash` the moment it has
 * the tokens, so reading the live hash from a component would be a race against that clear.
 */
const linkError = detectRecoveryErrorFromUrl(BOOT_HASH);

/** Has the session check come back yet, and did it find one? */
type SessionCheck = "checking" | "present" | "missing";

/**
 * Did the request never get an answer? The Supabase client gives exactly these statuses to a
 * request that could not reach the server (0) or hit a gateway failure (502 to 504), and it
 * never drops a session over them.
 */
const neverReachedServer = (error: AuthError) =>
  error.status === 0 || error.status === 502 || error.status === 503 || error.status === 504;

/**
 * Sign out, and say whether this browser really is signed out afterwards (D-047).
 *
 * Judged by what is left, not by what the sign-out call said, because the call reports an
 * error in three different situations:
 *
 *  - It never reached the server (offline). The session is still here. Say so at once: asking
 *    again would only sit through the client's retries, up to half a minute each.
 *  - The server answered no, usually because the session was already ended elsewhere (signed
 *    out on another device). The client keeps its local copy regardless, so without a second
 *    look nothing would let this person leave. A refresh settles it: the client drops a
 *    session the server has ended, and keeps one that is alive.
 *  - The session was dropped on the way (ended elsewhere, and its token expired). Nothing is
 *    left.
 *
 * Only "no session, and no trouble finding that out" counts as signed out. A refresh that
 * failed for want of a connection also finds no session, but reports an error, and the
 * session is still stored.
 */
async function signOutAndConfirm(): Promise<boolean> {
  let error: AuthError | null;
  try {
    ({ error } = await authClient.signOut());
  } catch {
    error = { message: "The sign-out failed unexpectedly." };
  }
  if (!error) return true;
  if (neverReachedServer(error)) return false;

  try {
    await authClient.refreshSession();
  } catch {
    // Judged below, by what is left.
  }
  try {
    const { session, error: checkError } = await authClient.getSession();
    return !session && !checkError;
  } catch {
    return false;
  }
}

/** One card, centred, so every state of this page looks like the login card next to it. */
const Shell = ({ children }: { children: React.ReactNode }) => (
  <div className="min-h-dvh flex items-center justify-center bg-gradient-to-br from-primary/10 via-background to-background p-4">
    <div className="w-full max-w-md">
      <div className="text-center mb-8">
        <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-primary text-primary-foreground font-extrabold text-2xl mb-4 shadow-[var(--glow-primary)]">
          B
        </div>
        <h1 className="text-3xl font-bold tracking-tight">
          Budge<span className="text-primary-text">AI</span>
        </h1>
      </div>
      <Card>{children}</Card>
    </div>
  </div>
);

const ResetPassword = () => {
  const navigate = useNavigate();
  const [sessionCheck, setSessionCheck] = useState<SessionCheck>("checking");
  const [userId, setUserId] = useState<string | null>(null);
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [pending, setPending] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Gate 1 wins outright, so when the URL already says the link is dead there is no
    // reason to look at the session at all (FR-009).
    if (linkError) return;

    let abandoned = false;
    void (async () => {
      let userIdFromSession: string | null = null;
      let found = false;
      try {
        const { session } = await authClient.getSession();
        found = !!session;
        userIdFromSession = session?.user?.id ?? null;
      } catch {
        // No session to be had. Treated the same as not finding one.
        found = false;
      }
      if (abandoned) return;
      setUserId(userIdFromSession);
      setSessionCheck(found ? "present" : "missing");
    })();

    return () => {
      abandoned = true;
    };
  }, []);

  const goToRequestForm = () => navigate(LOGIN_PATH, { replace: true });

  /**
   * FR-010: the visible way out. Signs out, clears the reset, back to the login card.
   *
   * Only once the sign-out has actually happened. When supabase-js cannot reach the server
   * (offline, a 5xx) it keeps the session. Leaving then would clear the reset while the user
   * is still signed in, and the next page they reach is the dashboard with the password never
   * set (SC-002). Staying is not the trap FR-010 guards against: nothing else works offline
   * either, and the button works again as soon as the connection does (D-047).
   */
  const handleCancel = async () => {
    // A second click must not start a second sign-out. The button is disabled too.
    if (leaving) return;
    setLeaving(true);
    const signedOut = await signOutAndConfirm();
    setLeaving(false);
    if (!signedOut) {
      setError("We couldn't sign you out. Check your connection and try again.");
      return;
    }
    endRecovery();
    goToRequestForm();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // A second click must not fire a second save. The button is disabled too; this is the
    // belt to its braces.
    if (pending) return;

    // Both checks answer locally, before any network call (FR-006). Mismatch first: it is
    // the more likely mistake, and reporting the length of a password the user did not mean
    // to type is noise.
    if (password !== confirmation) {
      setError("Those two passwords don't match.");
      return;
    }
    if (password.length < MIN_PASSWORD_LENGTH) {
      setError(`Use at least ${MIN_PASSWORD_LENGTH} characters.`);
      return;
    }

    setError(null);
    setPending(true);
    try {
      const { error: saveError } = await authClient.updateUser({ password });
      if (saveError) {
        // Supabase's own wording, not a generic one: the user cannot fix what they are not
        // told. No navigation, nothing that looks like success (FR-008).
        setError(saveError.message);
        setPending(false);
        return;
      }
      // Saved. The reset is over, and the dashboard is /app, not "/", which has been the
      // public landing page since D-039 (FR-007).
      endRecovery();
      navigate(APP_HOME, { replace: true });
    } catch (thrown) {
      setError(thrown instanceof Error ? thrown.message : "That didn't save. Please try again.");
      setPending(false);
    }
  };

  // Gate 1: the URL says the link is dead.
  if (linkError) {
    return (
      <Shell>
        <CardHeader>
          <CardTitle>That link no longer works</CardTitle>
          <CardDescription>Reset links expire, and each one can only be used once.</CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground mb-4">
            Ask for a new link and use the newest email you receive.
          </p>
          <Button className="w-full" onClick={goToRequestForm}>
            Get a new link
          </Button>
        </CardContent>
      </Shell>
    );
  }

  if (sessionCheck === "checking") {
    return (
      <Shell>
        <CardContent className="py-10 flex justify-center">
          <div className="animate-spin rounded-full h-10 w-10 border-4 border-primary/20 border-t-primary" />
        </CardContent>
      </Shell>
    );
  }

  // Gate 2: not a reset that belongs to whoever is using this browser (FR-004, FR-013).
  if (!isInRecovery(userId)) {
    return <Navigate to={APP_HOME} replace />;
  }

  // Gate 3: a reset is under way but the link never produced a session, so the form could
  // only fail. Say the link is dead and offer a new one (FR-013).
  if (sessionCheck === "missing") {
    return (
      <Shell>
        <CardHeader>
          <CardTitle>That link didn't sign you in</CardTitle>
          <CardDescription>
            It may have expired, or it may already have been used. Either way, a new password
            cannot be set from it.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button className="w-full" onClick={goToRequestForm}>
            Get a new link
          </Button>
        </CardContent>
      </Shell>
    );
  }

  return (
    <Shell>
      <CardHeader>
        <CardTitle>Choose a new password</CardTitle>
        <CardDescription>
          Set it here and you go straight to your dashboard, already signed in.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="new-password">New password</Label>
            <Input
              id="new-password"
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
            <p className="text-sm text-muted-foreground">
              At least {MIN_PASSWORD_LENGTH} characters.
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="confirm-password">Confirm new password</Label>
            <Input
              id="confirm-password"
              type="password"
              autoComplete="new-password"
              value={confirmation}
              onChange={(e) => setConfirmation(e.target.value)}
              required
            />
          </div>

          {error && (
            <p role="alert" className="text-sm text-destructive">
              {error}
            </p>
          )}

          {/*
            The label stays the same while the save is in flight, and the button is simply
            disabled. A label that changed to "Saving..." would move the one control the
            user is looking at, and it would hide which button this is.
          */}
          <Button type="submit" className="w-full" disabled={pending} aria-busy={pending}>
            Set my password
          </Button>

          <div className="text-center">
            <Button
              type="button"
              variant="link"
              onClick={handleCancel}
              disabled={leaving}
              aria-busy={leaving}
              className="text-sm text-muted-foreground"
            >
              Cancel and log in instead
            </Button>
          </div>
        </form>
      </CardContent>
    </Shell>
  );
};

export default ResetPassword;
